import { Button, Pagination } from '@mui/material';
import {
  DataGrid,
  useGridApiContext,
  useGridSelector,
  type GridColDef,
  type GridPaginationModel,
} from '@mui/x-data-grid';
import {
  gridPaginationModelSelector,
  gridRowCountSelector,
} from '@mui/x-data-grid';
import { useNavigate } from 'react-router';
import type { ResourceApprovalToReview } from '@/data/subsidy/resource-approvals/to-review/interfaces';
import { RefundStatus } from '@/models/subsidy/status';
import { formatDate } from '@/utils/format-date';
import { formatMoney } from '@/utils/format-money';

interface RefundApprovalsListProps {
  toReview: ResourceApprovalToReview[];
  pageState: {
    isLoading: boolean;
    data: ResourceApprovalToReview[];
    total: number;
    page: number;
    pageSize: number;
  };
  onPaginationModelChange: (newPage: GridPaginationModel) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSortModelChange: (model: any) => void;
}

interface HandleRowActionProps {
  resourceApprovalUuid: string;
  status: RefundStatus;
}

const handleRowAction = ({
  resourceApprovalUuid,
  status,
}: HandleRowActionProps) => {
  if (status === RefundStatus.ResourceApprovalPendingApproval) {
    return {
      variantType: 'contained',
      text: 'Analisar pedido',
      href: `avaliar-pedido/${resourceApprovalUuid}`,
    };
  } else {
    return {
      variantType: 'text',
      text: 'Ver detalhes',
      href: `detalhes/${resourceApprovalUuid}`,
    };
  }
};

const customLocaleText = {
  columnMenuFilter: 'Filtrar',
  columnMenuSelectAll: 'Selecionar todos',
  columnMenuUnselectAll: 'Desmarcar todos',
  columnMenuItemsPerPage: 'Itens por página:',
  columnMenuSortAsc: 'Ordenar crescente',
  columnMenuSortDesc: 'Ordenar decrescente',
  columnMenuHideColumn: 'Ocultar coluna',
  columnMenuManageColumn: 'Gerenciar colunas',
  noRowsLabel: 'Nenhum usuário encontrado',
};

const CustomPagination = () => {
  const apiRef = useGridApiContext();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);
  const totalPages = Math.ceil(rowCount / paginationModel.pageSize);

  return (
    <Pagination
      count={totalPages}
      page={paginationModel.page + 1}
      onChange={(_, value) => apiRef.current.setPage(value - 1)}
    />
  );
};

export const ToReviewList = ({
  toReview,
  pageState,
  onPaginationModelChange,
  onSortModelChange,
}: RefundApprovalsListProps) => {
  const navigate = useNavigate();
  const columns: GridColDef<ResourceApprovalToReview>[] = [
    {
      field: 'requestDate',
      headerName: 'Data de solicitação',
      flex: 1,
      sortable: true,
      sortingOrder: ['desc', 'asc'],
      valueGetter: (params) => formatDate(params.row.requested_at),
    },
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1,
      sortable: false,
      valueGetter: (params) => params.row.user.name,
    },
    {
      field: 'user',
      headerName: 'Usuário',
      flex: 1,
      sortable: false,
      valueGetter: (params) => params.row.user.email,
    },
    {
      field: 'refundableAmount',
      headerName: 'Valor reembolsável',
      flex: 1,
      sortable: false,
      valueGetter: (params) =>
        formatMoney(params.row.requested_refundable_amount),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 180,
      sortable: false,
      renderCell: (params) => {
        const { variantType, text, href } = handleRowAction({
          resourceApprovalUuid: params.row.uuid,
          status: params.row.status,
        });
        return (
          <Button
            onClick={() => navigate(href)}
            variant={variantType === 'contained' ? 'contained' : 'text'}
            fullWidth
          >
            {text}
          </Button>
        );
      },
    },
  ];

  return (
    <DataGrid
      rows={toReview}
      rowCount={pageState.total}
      loading={pageState.isLoading}
      getRowId={(row) => row.uuid}
      localeText={customLocaleText}
      columns={columns}
      disableRowSelectionOnClick
      onPaginationModelChange={onPaginationModelChange}
      onSortModelChange={onSortModelChange}
      autoHeight
      pagination
      paginationMode="server"
      paginationModel={{
        pageSize: pageState.pageSize,
        page: pageState.page - 1,
      }}
      slots={{ pagination: CustomPagination }}
    />
  );
};
